"use client";
import CheckBulletItem from "@/components/common/checkBulletItem/checkBulletItem.component";
import { PhoneFrame } from "@/components/marketing";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { formatInr } from "@/utils/basicFunctions.util";
import { Fragment } from "react";
import type { Tier, TierKey } from "../../data";
import { useTierStudio } from "./hooks";

interface TierStudioProps {
  tiers: Tier[];
  initialSpend: number;
  sliderMax: number;
  sliderStep: number;
  tablistLabel: string;
  benefitsLabel: string;
  eligibilityLabel: string;
  moveUpLabel: string;
  sliderLabel: string;
  sliderAriaLabel: string;
  flow: { title: string; text: string }[];
  phone: {
    title: string;
    tierLabel: string;
    benefitsLabel: string;
    topTierText: string;
    moreToReach: string;
    progressLabel: string;
  };
}

const tierFinish: Record<TierKey, { card: string; medal: string }> = {
  bronze: { card: "bg-tier-bronze", medal: "bg-medal-bronze" },
  silver: { card: "bg-tier-silver", medal: "bg-medal-silver" },
  gold: { card: "bg-tier-gold", medal: "bg-medal-gold" },
};

const medalClass =
  "grid place-items-center rounded-full text-white shadow-medal";

const labelClass = "mb-1.5 text-[13.5px] font-bold text-ink-muted";

// The preview's "tier studio": pick a tier card or drag the spend slider and
// the detail panel, three-step flow and the phone mock-up all follow.
const TierStudio = ({
  tiers,
  initialSpend,
  sliderMax,
  sliderStep,
  tablistLabel,
  benefitsLabel,
  eligibilityLabel,
  moveUpLabel,
  sliderLabel,
  sliderAriaLabel,
  flow,
  phone,
}: TierStudioProps) => {
  const studio = useTierStudio({
    tiers,
    initialSpend,
    moreToReachTemplate: phone.moreToReach,
    topTierText: phone.topTierText,
  });
  const { tier } = studio;

  return (
    <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,0.7fr)]">
      <div className="grid gap-[18px]">
        <div
          role="tablist"
          aria-label={tablistLabel}
          className="grid gap-3.5 md:grid-cols-3"
        >
          {tiers.map((item, index) => {
            const isActive = index === studio.activeIndex;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                id={`tier-tab-${item.key}`}
                aria-selected={isActive}
                aria-controls="tier-detail"
                tabIndex={isActive ? 0 : -1}
                onClick={() => studio.selectTier(index)}
                onKeyDown={(event) => studio.onTabKeyDown(event, index)}
                className={cn(
                  "grid cursor-pointer gap-1 rounded-[26px] border-[3px] border-transparent p-5 text-left text-ink shadow-card transition-[transform,border-color,box-shadow] duration-250 hover:-translate-y-1",
                  tierFinish[item.key].card,
                  isActive && "-translate-y-1.5 border-ink shadow-lift hover:-translate-y-1.5",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(medalClass, "mb-1.5 size-[46px] text-[22px]", tierFinish[item.key].medal)}
                >
                  ★
                </span>
                <span className="text-2xl font-bold tracking-[0.02em] uppercase">
                  {item.name}
                </span>
                <span className="mb-2 text-[14.5px] font-semibold">{item.how}</span>
                {item.cardBenefits.map((benefit) => (
                  <span
                    key={benefit}
                    className="relative pl-[22px] text-[14.5px] before:absolute before:left-0 before:font-extrabold before:content-['✓']"
                  >
                    {benefit}
                  </span>
                ))}
              </button>
            );
          })}
        </div>

        <div
          id="tier-detail"
          role="tabpanel"
          aria-labelledby={`tier-tab-${tier.key}`}
          aria-live="polite"
          className="grid gap-3.5 rounded-3xl border-2 border-line bg-white p-6"
        >
          <div className="flex flex-wrap items-baseline gap-3.5">
            <span className="text-[1.7rem] font-bold tracking-[-0.02em]">
              {tier.name}
            </span>
            <span className="font-semibold text-ink-muted">{tier.how}</span>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <h3 className={labelClass}>{benefitsLabel}</h3>
              <ul className="grid gap-2">
                {tier.cardBenefits.map((benefit) => (
                  <CheckBulletItem key={benefit}>{benefit}</CheckBulletItem>
                ))}
              </ul>
            </div>
            <div>
              <h3 className={labelClass}>{eligibilityLabel}</h3>
              <p className="mb-3 text-[15.5px]">{tier.eligibility}</p>
              <h3 className={labelClass}>{moveUpLabel}</h3>
            </div>
          </div>
        </div>

        <ol className="grid items-center gap-2.5 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
          {flow.map((step, index) => (
            <Fragment key={step.title}>
              {index > 0 && (
                <li
                  aria-hidden="true"
                  className="hidden h-[3px] w-[18px] bg-dash-primary md:block"
                />
              )}
              <li className="grid gap-0.5 rounded-2xl bg-cream px-3.5 py-3">
                <span className="grid size-[26px] place-items-center rounded-full bg-primary text-[13px] font-extrabold text-white">
                  {index + 1}
                </span>
                <span className="text-[14.5px] font-bold">{step.title}</span>
                <span className="text-[12.5px] text-ink-muted">{step.text}</span>
              </li>
            </Fragment>
          ))}
        </ol>

        <div className="grid gap-3 rounded-[18px] bg-tone-blush px-[18px] py-3.5 text-[15px] font-semibold">
          <span id="tier-spend-label">
            {sliderLabel}
            <output
              htmlFor="tier-spend"
              className="ml-1.5 font-extrabold text-primary"
            >
              {formatInr(studio.spend)}
            </output>
          </span>
          <Slider
            id="tier-spend"
            aria-label={sliderAriaLabel}
            min={0}
            max={sliderMax}
            step={sliderStep}
            value={[studio.spend]}
            onValueChange={([value]) => studio.setSpend(value)}
            className="[&_[data-slot=slider-track]]:bg-white"
          />
        </div>
      </div>

      {/* Phone first on smaller screens (stacked layout), beside the
          studio from xl. */}
      <div className="order-first xl:sticky xl:top-24 xl:order-none">
        <PhoneFrame title={phone.title}>
          <div
            className={cn(
              "flex items-center gap-3 rounded-2xl px-3.5 py-3 transition-[background] duration-400",
              tierFinish[tier.key].card,
            )}
          >
            <span
              aria-hidden="true"
              className={cn(medalClass, "size-[42px] text-xl", tierFinish[tier.key].medal)}
            >
              ★
            </span>
            <div>
              <span className="block text-[11px] text-ink-muted">
                {phone.tierLabel}
              </span>
              <span className="block text-[1.2rem] font-bold tracking-[0.04em]">
                {tier.name.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="grid gap-1.5 rounded-[14px] border border-line p-3">
            <Progress
              value={studio.progress}
              aria-label={phone.progressLabel}
              className="h-2.5 bg-paper"
              indicatorClassName="bg-primary duration-500"
            />
            <span className="text-[1.25rem] font-bold">{studio.amountText}</span>
            <span className="text-xs text-ink-muted">{studio.moreText}</span>
          </div>
          <span className="mt-1 text-[13px] font-bold">{phone.benefitsLabel}</span>
          <ul className="grid gap-1.5 rounded-[14px] border border-line px-3 py-2.5">
            {tier.phoneBenefits.map((benefit) => (
              <CheckBulletItem key={benefit}>{benefit}</CheckBulletItem>
            ))}
          </ul>
        </PhoneFrame>
      </div>
    </div>
  );
};

export default TierStudio;
